/**
 * NumberingPatternController — onglet « Numérotation » de /parametres.html
 * (EPIC-041 / 41.2, §7.A).
 *
 * Pilote le formulaire de format de numérotation des factures émises :
 *   - hydratation initiale via GET /numbering-pattern (pattern courant
 *     + dernier numéro émis) ;
 *   - widget « composants » réordonnable (boutons ▲▼) avec ajout / retrait —
 *     `counter` obligatoire (non retirable), `year_short` / `year_full`
 *     mutuellement exclusifs ;
 *   - aperçu en direct du prochain numéro via GET /numbering-pattern/preview,
 *     en debounce (aucun appel API à chaque frappe) ;
 *   - garde-fou client de cohérence reset/composants, en amont du 422 serveur ;
 *   - enregistrement via PUT /numbering-pattern — le 422 (rupture de
 *     séquence ou facture récurrente active/en pause) affiche le message
 *     backend tel quel dans le bandeau d'erreur.
 *
 * Convention CLAUDE.md respectée :
 *   - Pas d'HTML inline en JS : les lignes du widget sont clonées depuis
 *     <template id="tpl-numbering-component"> via cloneTemplate / fillSlots.
 *   - Aucune classe CSS inventée.
 */

import BaseController from "./base_controller.js";
import { api } from "/js/services/api.js";

// Libellé + valeur d'exemple de chaque composant. L'aperçu réel du numéro est
// calculé côté serveur ; ces exemples n'illustrent que la ligne du widget.
const COMPONENT_META = {
  year_full: { name: "Année — format long", example: "2026" },
  year_short: { name: "Année — format court", example: "26" },
  month: { name: "Mois", example: "05" },
  counter: { name: "Compteur", example: "0247" },
};

// Pattern legacy (= backfill 41.1) — fallback si le GET initial échoue.
const DEFAULT_PATTERN = {
  prefix: "F",
  components: ["year_full", "counter"],
  separator: "-",
  counterPadding: 4,
  resetStrategy: "annual",
  counterStartAt: 1,
};

const RESET_STRATEGIES = ["annual", "monthly", "never"];
const PREVIEW_DEBOUNCE_MS = 350;

export default class NumberingPatternController extends BaseController {
  static targets = [
    "prefixInput", "separatorInput", "paddingInput", "resetInput", "startInput",
    "componentsList", "addSelect",
    "previewValue", "lastNumber", "errorBanner", "submitButton",
  ];

  // Ordre courant des composants — source de vérité du widget.
  components = [];
  // Dernier état serveur — référence pour « Annuler ».
  serverState = { ...DEFAULT_PATTERN };

  async connect() {
    let lastNumber = null;
    try {
      const response = await api.getNumberingPattern();
      this.serverState = this._normalize(response && response.data);
      lastNumber = (response && response.lastNumber) || null;
    } catch (_error) {
      // 401 déjà redirigé par api.js. Sinon : on retombe sur le pattern
      // legacy pour garder le widget fonctionnel et on prévient l'utilisateur.
      this.showError("Impossible de charger le format de numérotation actuel.");
    }
    this._hydrate(this.serverState);
    this._renderLastNumber(lastNumber);
    this._refreshPreview();
  }

  disconnect() {
    clearTimeout(this.previewTimer);
  }

  // ---------------------------------------------------------------------------
  // Widget « composants » — réordonnancement / ajout / retrait
  // ---------------------------------------------------------------------------

  moveUp(event) {
    const component = this._rowComponent(event);
    const index = this.components.indexOf(component);
    if (index <= 0) return;
    this.components.splice(index - 1, 0, this.components.splice(index, 1)[0]);
    this._renderComponents();
    this._focusComponent(component);
    this.schedulePreview();
  }

  moveDown(event) {
    const component = this._rowComponent(event);
    const index = this.components.indexOf(component);
    if (index === -1 || index >= this.components.length - 1) return;
    this.components.splice(index + 1, 0, this.components.splice(index, 1)[0]);
    this._renderComponents();
    this._focusComponent(component);
    this.schedulePreview();
  }

  removeComponent(event) {
    const component = this._rowComponent(event);
    if (component === "counter") return; // Garde-fou : compteur non retirable.
    this.components = this.components.filter((c) => c !== component);
    this._renderComponents();
    if (this.hasAddSelectTarget) this.addSelectTarget.focus();
    this.schedulePreview();
  }

  addComponent() {
    if (!this.hasAddSelectTarget) return;
    const value = this.addSelectTarget.value;
    this.addSelectTarget.value = "";
    if (!value || this.components.includes(value)) return;
    // Insertion juste avant le compteur (ordre naturel préfixe › date › compteur).
    const counterIndex = this.components.indexOf("counter");
    if (counterIndex === -1) {
      this.components.push(value);
    } else {
      this.components.splice(counterIndex, 0, value);
    }
    this._renderComponents();
    this.schedulePreview();
  }

  // ---------------------------------------------------------------------------
  // Aperçu live (debounce)
  // ---------------------------------------------------------------------------

  schedulePreview() {
    clearTimeout(this.previewTimer);
    this.previewTimer = setTimeout(() => this._refreshPreview(), PREVIEW_DEBOUNCE_MS);
  }

  async _refreshPreview() {
    const pattern = this._readForm();
    const coherenceError = this._coherenceError(pattern);
    if (coherenceError) {
      this._showError(coherenceError);
      this.previewValueTarget.textContent = "—";
      return;
    }
    try {
      const response = await api.previewNumberingPattern(pattern);
      this._hideError();
      this.previewValueTarget.textContent = (response && response.preview) || "—";
    } catch (error) {
      this.previewValueTarget.textContent = "—";
      this._showError(this._errorMessage(error, "Aperçu indisponible pour ce format."));
    }
  }

  // ---------------------------------------------------------------------------
  // Enregistrement / annulation
  // ---------------------------------------------------------------------------

  async save(event) {
    event.preventDefault();
    const pattern = this._readForm();
    const coherenceError = this._coherenceError(pattern);
    if (coherenceError) {
      this._showError(coherenceError);
      return;
    }
    this.submitButtonTarget.disabled = true;
    try {
      const response = await api.updateNumberingPattern(pattern);
      this.serverState = this._normalize(response && response.data);
      this._hideError();
      this._hydrate(this.serverState);
      this._refreshPreview();
      this.showSuccess("Format de numérotation enregistré.");
    } catch (error) {
      this._showError(this._errorMessage(error, "Enregistrement du format impossible."));
    } finally {
      this.submitButtonTarget.disabled = false;
    }
  }

  reset(event) {
    event.preventDefault();
    this._hideError();
    this._hydrate(this.serverState);
    this._refreshPreview();
  }

  // ---------------------------------------------------------------------------
  // Hydration / rendu
  // ---------------------------------------------------------------------------

  _hydrate(state) {
    this.prefixInputTarget.value = state.prefix;
    this.separatorInputTarget.value = state.separator;
    this.paddingInputTarget.value = String(state.counterPadding);
    this.resetInputTarget.value = state.resetStrategy;
    this.startInputTarget.value = String(state.counterStartAt);
    this.components = [...state.components];
    this._renderComponents();
  }

  _renderComponents() {
    while (this.componentsListTarget.firstChild) {
      this.componentsListTarget.removeChild(this.componentsListTarget.firstChild);
    }
    this.components.forEach((component, index) => {
      const meta = COMPONENT_META[component];
      if (!meta) return;
      const fragment = this.cloneTemplate("tpl-numbering-component");
      const row = fragment.querySelector("li");
      row.dataset.component = component;
      this.fillSlots(fragment, {
        name: meta.name,
        example: meta.example,
        note: component === "counter" ? "· obligatoire" : "",
      });
      const [upButton, downButton, removeButton] = row.querySelectorAll("button");
      upButton.disabled = index === 0;
      downButton.disabled = index === this.components.length - 1;
      upButton.setAttribute("aria-label", `Déplacer « ${meta.name} » vers le haut`);
      downButton.setAttribute("aria-label", `Déplacer « ${meta.name} » vers le bas`);
      if (component === "counter") {
        removeButton.hidden = true;
      } else {
        removeButton.setAttribute("aria-label", `Retirer « ${meta.name} »`);
      }
      this.componentsListTarget.appendChild(fragment);
    });
    this._refreshAddOptions();
  }

  // Désactive les options déjà présentes ; les deux formats d'année sont
  // exclusifs — si l'un est présent, l'autre est désactivé à l'ajout.
  _refreshAddOptions() {
    if (!this.hasAddSelectTarget) return;
    const hasYear = this.components.includes("year_full")
      || this.components.includes("year_short");
    for (const option of this.addSelectTarget.options) {
      if (!option.value) continue;
      option.disabled = option.value === "month"
        ? this.components.includes("month")
        : hasYear;
    }
  }

  _renderLastNumber(lastNumber) {
    this.lastNumberTarget.textContent = lastNumber || "—";
  }

  // ---------------------------------------------------------------------------
  // Helpers
  // ---------------------------------------------------------------------------

  _readForm() {
    return {
      prefix: this.prefixInputTarget.value,
      components: [...this.components],
      separator: this.separatorInputTarget.value,
      counterPadding: this._intValue(this.paddingInputTarget, 4),
      resetStrategy: this.resetInputTarget.value,
      counterStartAt: this._intValue(this.startInputTarget, 1),
    };
  }

  // Cohérence reset/composants vérifiée côté client avant l'appel API : une
  // réinitialisation périodique exige le(s) composant(s) de date correspondant(s).
  _coherenceError(pattern) {
    const hasYear = pattern.components.includes("year_full")
      || pattern.components.includes("year_short");
    const hasMonth = pattern.components.includes("month");
    if (pattern.resetStrategy === "annual" && !hasYear) {
      return "Une réinitialisation annuelle exige un composant d'année dans le numéro.";
    }
    if (pattern.resetStrategy === "monthly" && (!hasYear || !hasMonth)) {
      return "Une réinitialisation mensuelle exige un composant d'année et un composant de mois.";
    }
    return null;
  }

  _normalize(data) {
    const source = data && typeof data === "object" ? data : {};
    const components = Array.isArray(source.components)
      ? source.components.filter((c) => COMPONENT_META[c])
      : [];
    if (components.length === 0) components.push(...DEFAULT_PATTERN.components);
    if (!components.includes("counter")) components.push("counter");
    return {
      prefix: typeof source.prefix === "string" ? source.prefix : DEFAULT_PATTERN.prefix,
      components,
      separator: typeof source.separator === "string"
        ? source.separator
        : DEFAULT_PATTERN.separator,
      counterPadding: this._toInt(source.counterPadding, DEFAULT_PATTERN.counterPadding),
      resetStrategy: RESET_STRATEGIES.includes(source.resetStrategy)
        ? source.resetStrategy
        : DEFAULT_PATTERN.resetStrategy,
      counterStartAt: this._toInt(source.counterStartAt, DEFAULT_PATTERN.counterStartAt),
    };
  }

  _intValue(input, fallback) {
    return this._toInt(input ? input.value : null, fallback);
  }

  _toInt(value, fallback) {
    const parsed = Number.parseInt(value, 10);
    return Number.isFinite(parsed) ? parsed : fallback;
  }

  _rowComponent(event) {
    const row = event.currentTarget.closest("li");
    return row ? row.dataset.component : null;
  }

  _focusComponent(component) {
    const row = this.componentsListTarget
      .querySelector(`li[data-component="${component}"]`);
    if (!row) return;
    const button = row.querySelector("button:not([disabled])");
    if (button) button.focus();
  }

  _errorMessage(error, fallback) {
    return error && error.message ? error.message : fallback;
  }

  _showError(message) {
    this.errorBannerTarget.textContent = message;
    this.errorBannerTarget.hidden = false;
  }

  _hideError() {
    this.errorBannerTarget.hidden = true;
    this.errorBannerTarget.textContent = "";
  }
}
